'use client';

import {
  Box,
  Label,
  Button,
  IconButton,
  SystemIcon,
  Typography,
  Modal,
} from '@wishket/design-system';
import dynamic from 'next/dynamic';
import { Sheet } from 'react-modal-sheet';
import { useEffect, useState } from 'react';

import CollectionUpdateModalLottieData from './collection_update_modal_lottie.json';

import { UIOverlayModalProps } from '@/shared/types';

const Lottie = dynamic(() => import('react-lottie'), { ssr: false });

// Base interface for common handler functions
interface CollectionModalHandlers {
  handleClose: () => void;
  handleSubmit: () => void;
}

type CollectionUpdateModalItemProps = CollectionModalHandlers;

type CollectionUpdateModalProps = CollectionModalHandlers & UIOverlayModalProps;

export const DesktopCollectionUpdateModal = ({
  isOpen,
  handleClose,
  handleSubmit,
  rootId,
}: CollectionUpdateModalProps) => {
  return (
    <Modal rootId={rootId} isOpen={isOpen} onClose={handleClose} size="sm">
      <CollectionUpdateModalItem
        handleClose={handleClose}
        handleSubmit={handleSubmit}
      />
    </Modal>
  );
};

export const MobileCollectionUpdateModal = ({
  isOpen,
  handleClose,
  handleSubmit,
}: CollectionUpdateModalProps) => {
  return (
    <Sheet
      isOpen={isOpen}
      initialSnap={0}
      snapPoints={[464]}
      onClose={handleClose}
    >
      <Sheet.Backdrop onTap={handleClose} />
      <Sheet.Container className="react-modal-sheet-container">
        <Sheet.Header className="h-fit rounded-t-3xl bg-w-gray-10" />
        <Sheet.Content>
          <CollectionUpdateModalItem
            handleClose={handleClose}
            handleSubmit={handleSubmit}
          />
        </Sheet.Content>
      </Sheet.Container>
    </Sheet>
  );
};

export const CollectionUpdateModalItem = ({
  handleClose,
  handleSubmit,
}: CollectionUpdateModalItemProps) => {
  const [autoplay, setAutoPlay] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAutoPlay(true);
    }, 600);

    return () => clearTimeout(timer);
  }, []);

  return (
    <Box className="h-fit desktop:w-[400px]">
      <Box className="relative flex h-[200px] w-full flex-col overflow-hidden bg-w-gray-10 px-8 pt-6 desktop:h-[300px] ">
        <Box className="z-10 hidden w-full items-center justify-between desktop:flex">
          <Label.Root canHover color="primary" type="outlined">
            <Label.Text>업데이트</Label.Text>
          </Label.Root>
          <IconButton onClick={handleClose}>
            <SystemIcon name="large_delete" className="text-w-gray-600" />
          </IconButton>
        </Box>
        <Lottie
          options={{
            autoplay,
            loop: false,
            animationData: CollectionUpdateModalLottieData,
          }}
          style={{
            left: 0,
            bottom: 0,
            height: 300,
            cursor: 'default',
            position: 'absolute',
          }}
        />
      </Box>
      <Box className="w-full rounded-b-3xl bg-white p-8">
        <Box className="mb-8 flex flex-col items-center">
          <Typography variant="subTitle1" className="mb-3 font-medium">
            다른 독자들의 컬렉션을 만나보세요!
          </Typography>
          <Typography variant="body1" className="text-w-gray-600">
            다른 독자들의 업무 꿀팁과 노하우를
          </Typography>
          <Typography variant="body1" className="text-w-gray-600">
            컬렉션에서 확인해 보세요.
          </Typography>
        </Box>
        <Button variant="solid" className="w-full" onClick={handleSubmit}>
          컬렉션 보러가기
        </Button>
      </Box>
    </Box>
  );
};
